import { Button, Flex, Icon, List, ListItem, Text } from '@chakra-ui/react';
import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import { IconChevronRight } from 'central-icons/IconChevronRight';

import { FLAG_ENABLE_RICS } from '@revelio/core';

import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import {
  GEOGRAPHY_GRANULARITY_FILTERS,
  RICS_FILTERS,
  ROLE_GRANULARITY_FILTERS,
} from '../../constants/filter-constants';
import { FilterId } from '../../types/filters';

export interface Segment {
  label: string;
  value: string[];
}

export const mapSegmentOptionToFilterId = (
  segment: Segment
): FilterId | null => {
  switch (segment.label) {
    case 'Ethnicity':
      return FilterId.ETHNICITY;
    case 'Gender':
      return FilterId.GENDER;
    case 'Geography':
      return FilterId.GEOGRAPHY;
    case 'Highest Degree':
      return FilterId.EDUCATION;
    case 'Industry':
      return FilterId.INDUSTRY;
    case 'Role':
      return FilterId.ROLE;
    case 'Seniority':
      return FilterId.SENIORITY;
    default:
      return null;
  }
};

export const mapSegmentOptionToKey = (segment: Segment): string | null => {
  switch (segment.label) {
    case 'Ethnicity':
      return 'ethnicity';
    case 'Gender':
      return 'gender';
    case 'Geography':
      return 'geography';
    case 'Highest Degree':
      return 'highest_degree';
    case 'Industry':
      return 'industry';
    case 'Role':
      return 'role';
    case 'Seniority':
      return 'seniority';
    default:
      return null;
  }
};

export const mapSegmentKeyToLabel = (
  segmentKey: string | null
): string | null => {
  if (!segmentKey) return null;
  const option = segmentOptions.find((opt) => {
    const key = mapSegmentOptionToKey(opt);
    return key === segmentKey || opt.value.includes(segmentKey);
  });
  return option?.label ?? null;
};

export const segmentOptions: Segment[] = [
  { label: 'Ethnicity', value: ['ethnicity'] },
  { label: 'Gender', value: ['gender'] },
  { label: 'Geography', value: GEOGRAPHY_GRANULARITY_FILTERS },
  { label: 'Highest Degree', value: ['highest_degree'] },
  {
    label: 'Industry',
    value: FLAG_ENABLE_RICS ? RICS_FILTERS : ['industry'],
  },
  { label: 'Role', value: ROLE_GRANULARITY_FILTERS },
  { label: 'Seniority', value: ['seniority'] },
];

interface SegmentSelectorProps {
  onSegmentSelect: (segment: Segment) => void;
  onClickBack: () => void;
}

export const SegmentSelector = ({
  onSegmentSelect,
  onClickBack,
}: SegmentSelectorProps) => {
  const { revelioPageId } = useTabContentConfig();
  const isCompanyScreener = revelioPageId === RevelioPageId.COMPANY_SCREENER_V2;

  return (
    <Flex direction="column" fontSize="14px">
      {/* SEGMENTS Header with back arrow */}
      <Flex paddingX={0.5} pb={2} alignItems="center">
        <Flex
          width="20px"
          height="20px"
          _hover={{
            background: '#2D426A1A',
          }}
          borderRadius="50%"
          justifyContent="center"
          alignItems="center"
        >
          <Icon
            as={IconArrowLeft}
            boxSize={4}
            cursor="pointer"
            onClick={onClickBack}
            data-testid="segment-selector-back"
          />
        </Flex>
        <Text fontSize={12} fontWeight={600} pl={1.5} textTransform="uppercase">
          SEGMENTS
        </Text>
      </Flex>

      {/* Segment list */}
      <List paddingLeft={1}>
        {segmentOptions
          .filter(
            // No industry segment on company screener
            (option) => !(isCompanyScreener && option.label === 'Industry')
          )
          .map((option, index) => (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              onClick={() => onSegmentSelect(option)}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              p={1}
              key={index}
            >
              <ListItem
                style={{ cursor: 'pointer' }}
                data-testid={`segment-option-${option.label}`}
              >
                {option.label}
              </ListItem>
              <Icon as={IconChevronRight} />
            </Flex>
          ))}
      </List>

      {/* Back link at bottom */}
      <Flex justifyContent="flex-end" alignItems="center" pt={2}>
        <Button
          variant="link"
          fontSize="12px"
          height="32px"
          colorScheme="gray"
          onClick={onClickBack}
          autoFocus
        >
          Back
        </Button>
      </Flex>
    </Flex>
  );
};
